import React from "react";

import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import ScrollingBanner from "@/components/global/ui/ScrollingBanner";

import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";

import bg from "@/public/global/squares-bg-light.svg";
import arrowDown from "@/public/manifesto/arrow-down-icon.svg";

export default function Vision({ data }: { data: PageManifestoSchema }) {
  return (
    <section className="pt-[4rem] lg:pt-[12rem] bg-[#0A1A1D] overflow-clip relative">
      {/* vertical lines */}
      <div className="w-full h-full max-w-[179rem] flex justify-between absolute left-1/2 -translate-x-1/2 top-0 px-[1rem] lg:px-0 pointer-events-none">
        <div className="w-[.375rem] min-w-[.375rem] flex h-full min-h-full">
          <span className="w-[0.125rem] min-w-[0.125rem] h-full min-h-full bg-white/10"></span>
          <span className="w-[0.25rem] min-w-[0.25rem] h-full min-h-full bg-black/20"></span>
        </div>

        <div className="w-[.375rem] min-w-[.375rem] flex h-full min-h-full">
          <span className="w-[0.125rem] min-w-[0.215rem] h-full min-h-full bg-white/10"></span>
          <span className="w-[0.25rem] min-w-[0.25rem] h-full min-h-full bg-black/20"></span>
        </div>
      </div>
      {/* end vertical lines */}
      {/* background */}
      <Image
        src={bg}
        alt="Background"
        className="absolute w-[150%] lg:w-full max-w-[120rem] left-1/2 -translate-x-1/2 top-0 pointer-events-none"
      />
      {/* end background */}
      <div className="w-full flex flex-col justify-center items-center">
        <div className="w-fit flex items-center bg-white mb-[2rem]">
          <div className="w-[4rem] h-full bg-[#D9F9B9] p-[1rem]">
            <Image src={arrowDown} alt="Arrow down" className="w-full" />
          </div>
          <span className="font-geist text-black text-[2rem] font-[400] leading-[100%] px-[1rem] py-[1rem]">
            {data?.settings?.language === "en" ? "Our " : "Unsere "}
            Vision
          </span>
        </div>

        <div className="w-full max-w-[100rem] mx-auto px-[2rem] lg:px-0">
          {data?.visionTexts && (
            <PortableText
              value={data?.visionTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="font-funnel text-white text-[4.5rem] lg:text-[6rem] leading-[100%] font-[500] mb-[2rem] text-center">
                      {children}
                    </h2>
                  ),
                  normal: ({ children }) => (
                    <p className="text-white/60 font-[400] leading-[150%] mb-0 text-[2.25rem] text-center">
                      {children}
                    </p>
                  ),
                },
                marks: {
                  greenBackground: ({ children }) => (
                    <span className="font-ivy !font-[300] text-[#D9F9B9] decoration-[#D9F9B9]/20 [&_u]:!decoration-1 [&_u]:!underline-offset-4 bg-[#D9F9B9]/20 leading-[100%] inline-block h-full">
                      {children}
                    </span>
                  ),
                },
              })}
            />
          )}
        </div>

        {/* lines */}
        <div className="h-[.375rem] min-h-[.375rem] flex flex-col w-[179rem] min-w-[179rem] mx-auto overflow-clip lg:mb-[5rem] mb-[4rem] mt-[2rem] lg:mt-[3rem]">
          <span className="w-full min-w-full h-[.125rem] min-h-[.125rem] bg-white/10"></span>
          <span className="w-full min-w-full h-[.25rem] min-h-[.25rem] bg-black/20"></span>
        </div>
        {/* end lines */}

        <div className="w-full max-w-[110rem] mx-auto flex flex-col gap-[1rem] px-[2rem] lg:px-0">
          {data?.visionList &&
            data?.visionList?.map((item, index) => (
              <div
                key={index}
                className="w-[calc(100%-3rem)] lg:w-full flex justify-between items-stretch bg-[#112628] p-[1rem] lg:ps-[3rem] border-l-[.25rem] border-l-[#D9F9B9] ms-[3rem] relative"
              >
                {/*  */}
                <div
                  className="absolute top-0 left-[-3.25rem] w-fit px-[1rem] pb-[1.5rem] pt-[2rem] bg-[#D9F9B9] min-w-[3rem]"
                  style={{
                    clipPath:
                      "polygon(0 0, 100% 0, 100% 100%, 40% 100%, 0 75%)",
                  }}
                >
                  <span className="font-geist text-black text-[2rem] font-[500] leading-[100%] uppercase">
                    {index + 1}
                  </span>
                </div>
                {/*  */}
                <div className="flex flex-col px-[1rem]">
                  <span className="text-white text-[2.5rem] font-[400] leading-[150%]">
                    {item.title}
                  </span>
                  <span className="text-white/60 text-[2.25rem] font-[400] leading-[150%]">
                    {item.text}
                  </span>
                </div>

                <div className="bg-[#D9F9B9]/10 p-[1rem] lg:p-[2rem] w-fit h-auto min-w-fit min-h-full flex justify-center items-center">
                  <GroqImage
                    src={item.icon}
                    alt="icon"
                    className="!w-[4rem] !min-w-[4rem] !h-[4rem] !min-h-[4rem] !lg:w-[5rem] !lg:min-w-[5rem] !lg:h-[5rem] !lg:min-h-[5rem]"
                  />
                </div>
              </div>
            ))}
        </div>

        {/* lines */}
        <div className="h-[.375rem] min-h-[.375rem] flex flex-col w-[179rem] min-w-[179rem] mx-auto overflow-clip lg:mt-[6rem] lg:my-[5rem] my-[3rem]">
          <span className="w-full min-w-full h-[.125rem] min-h-[.125rem] bg-white/10"></span>
          <span className="w-full min-w-full h-[.25rem] min-h-[.25rem] bg-black/20"></span>
        </div>
        {/* end lines */}

        <div className="w-full max-w-[85rem] mx-auto px-[2rem] lg:px-0 mb-[5rem] lg:mb-[15rem]">
          {data?.visionFooterTexts && (
            <PortableText
              value={data?.visionFooterTexts ?? []}
              components={customRichTextComponent({
                block: {
                  normal: ({ children }) => (
                    <p className="text-white/60 font-[400] leading-[150%] mb-0 text-[2.25rem] text-center">
                      {children}
                    </p>
                  ),
                },
                marks: {
                  strong: ({ children }) => (
                    <span className="text-white">{children}</span>
                  ),
                },
              })}
            />
          )}
        </div>
      </div>

      <ScrollingBanner text={data?.visionScroll ?? "Sei Teil der Revolution"} />
    </section>
  );
}
